import { createContext, useContext, type JSX, type ReactNode } from 'react'
import type { IconGlyph as TenantIconGlyph } from '@fitting-room/contracts'

/**
 * The renderer's PUBLISHED ICON VOCABULARY.
 *
 * A flow option carries `icon: string` — a name, chosen by whoever authored
 * the tenant's data. This registry is the set of names this client knows how
 * to draw, and every glyph in it is a neutral geometric mark: nothing here
 * names a garment shape, a body, a face or a colour, because the client is
 * not allowed to know what the boutique sells.
 *
 * A name is resolved in three steps, in this order:
 *
 *   1. the TENANT'S OWN drawings, shipped as data on `tenant.iconSet`. This is
 *      where a bridal silhouette or a neckline lives — beside the attribute
 *      definitions it serves, so a boutique selling something else replaces it
 *      without a deploy and this file stays honest about knowing nothing.
 *   2. the neutral vocabulary below, for a tenant that ships no drawing.
 *   3. `MonogramGlyph`, which draws a deterministic frame plus the option's own
 *      initial — a text fallback that still gives each option a distinct,
 *      stable mark instead of a blank square.
 */
type Glyph = (props: { readonly size: number }) => JSX.Element

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.25,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

function frame(size: number, children: JSX.Element): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      {children}
    </svg>
  )
}

export const ICON_VOCABULARY = [
  'dot',
  'ring',
  'disc',
  'arc',
  'arch',
  'chevron',
  'wave',
  'zigzag',
  'grid',
  'stack',
  'column',
  'beam',
  'lozenge',
  'facet',
  'spark',
  'burst',
  'crescent',
  'droplet',
  'petal',
  'lattice',
  'ripple',
  'shard',
] as const

export type IconName = (typeof ICON_VOCABULARY)[number]

const glyphs: Record<IconName, Glyph> = {
  dot: ({ size }) => frame(size, <circle cx="24" cy="24" r="5" fill="currentColor" />),
  ring: ({ size }) => frame(size, <circle cx="24" cy="24" r="13" {...stroke} />),
  disc: ({ size }) =>
    frame(
      size,
      <>
        <circle cx="24" cy="24" r="13" fill="currentColor" opacity="0.18" />
        <circle cx="24" cy="24" r="13" {...stroke} />
      </>,
    ),
  arc: ({ size }) => frame(size, <path d="M9 34a15 15 0 0 1 30 0" {...stroke} />),
  arch: ({ size }) => frame(size, <path d="M12 38V25a12 12 0 0 1 24 0v13" {...stroke} />),
  chevron: ({ size }) => frame(size, <path d="M12 18l12 11 12-11M12 28l12 11 12-11" {...stroke} />),
  wave: ({ size }) => frame(size, <path d="M8 27c5-9 11-9 16 0s11 9 16 0" {...stroke} />),
  zigzag: ({ size }) => frame(size, <path d="M8 30l8-11 8 11 8-11 8 11" {...stroke} />),
  grid: ({ size }) =>
    frame(
      size,
      <>
        <rect x="11" y="11" width="26" height="26" {...stroke} />
        <path d="M11 24h26M24 11v26" {...stroke} />
      </>,
    ),
  stack: ({ size }) => frame(size, <path d="M10 16h28M10 24h28M10 32h28" {...stroke} />),
  column: ({ size }) => frame(size, <path d="M18 10h12M20 10v28M28 10v28M16 38h16" {...stroke} />),
  beam: ({ size }) => frame(size, <path d="M24 8v32M14 14l10-6 10 6" {...stroke} />),
  lozenge: ({ size }) => frame(size, <path d="M24 9l12 15-12 15-12-15z" {...stroke} />),
  facet: ({ size }) =>
    frame(
      size,
      <>
        <path d="M24 9l13 9v12l-13 9-13-9V18z" {...stroke} />
        <path d="M11 18l13 9 13-9M24 27v12" {...stroke} />
      </>,
    ),
  spark: ({ size }) => frame(size, <path d="M24 9v30M11 24h26M15 15l18 18M33 15L15 33" {...stroke} />),
  burst: ({ size }) =>
    frame(
      size,
      <>
        <circle cx="24" cy="24" r="6" {...stroke} />
        <path d="M24 6v6M24 36v6M6 24h6M36 24h6M12 12l4 4M32 32l4 4M36 12l-4 4M16 32l-4 4" {...stroke} />
      </>,
    ),
  crescent: ({ size }) => frame(size, <path d="M31 11a14 14 0 1 0 0 26 16 16 0 0 1 0-26z" {...stroke} />),
  droplet: ({ size }) => frame(size, <path d="M24 8c7 10 11 14 11 20a11 11 0 1 1-22 0c0-6 4-10 11-20z" {...stroke} />),
  petal: ({ size }) => frame(size, <path d="M24 8c9 6 12 12 12 18s-5 12-12 14c-7-2-12-8-12-14S15 14 24 8z" {...stroke} />),
  lattice: ({ size }) => frame(size, <path d="M10 18l14-8 14 8-14 8zM10 30l14-8M38 30l-14-8M10 18v12l14 8 14-8V18" {...stroke} />),
  ripple: ({ size }) =>
    frame(
      size,
      <>
        <circle cx="24" cy="24" r="5" {...stroke} />
        <circle cx="24" cy="24" r="10" {...stroke} opacity="0.7" />
        <circle cx="24" cy="24" r="15" {...stroke} opacity="0.4" />
      </>,
    ),
  shard: ({ size }) => frame(size, <path d="M14 38L24 8l10 30-10-7z" {...stroke} />),
}

function isKnown(name: string): name is IconName {
  return Object.prototype.hasOwnProperty.call(glyphs, name)
}

/** Stable, order-independent hash so the same icon name always draws the same frame. */
function hash(value: string): number {
  let h = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h)
}

const frames = ['ring', 'lozenge', 'arch', 'facet', 'droplet', 'petal', 'shard', 'disc'] as const satisfies readonly IconName[]

interface MonogramProps {
  readonly seed: string
  readonly initial: string
  readonly size: number
}

function MonogramGlyph({ seed, initial: letter, size }: MonogramProps): JSX.Element {
  const chosen = frames[hash(seed) % frames.length] ?? 'ring'
  const Frame = glyphs[chosen]
  return (
    <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <span className="absolute inset-0 opacity-45">
        <Frame size={size} />
      </span>
      <span
        className="fr-display relative"
        style={{ fontSize: size * 0.42, lineHeight: 1, color: 'inherit' }}
        aria-hidden="true"
      >
        {letter}
      </span>
    </span>
  )
}

/* --------------------------- the tenant's own set ------------------------- */

export type IconSet = Readonly<Record<string, TenantIconGlyph>>

const IconSetContext = createContext<IconSet>({})

/**
 * Puts the tenant's drawings in reach of every option tile below it. A context
 * rather than a prop because `icon` is authored on options several layers down,
 * and threading a whole icon set through each step renderer would make every
 * one of them know about icons.
 */
export function IconSetProvider({ set, children }: { readonly set: IconSet; readonly children: ReactNode }): JSX.Element {
  return <IconSetContext.Provider value={set}>{children}</IconSetContext.Provider>
}

export function useIconSet(): IconSet {
  return useContext(IconSetContext)
}

/**
 * One tenant drawing. Stroked with `currentColor` and never filled, so the
 * tile's own selected state — which turns the colour gold — carries the mark
 * with it, and `.fr-tile[data-selected] svg` thickens the pen to 1.6.
 */
function DrawnGlyph({ glyph, size }: { readonly glyph: TenantIconGlyph; readonly size: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox={glyph.viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.1}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {glyph.paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  )
}

export interface IconGlyphProps {
  /** Icon name from tenant data. Unknown names fall back to the monogram. */
  readonly name?: string | undefined
  /** The option's own label — the text fallback and the monogram's source. */
  readonly label: string
  readonly size?: number
  readonly className?: string
}

/**
 * A tenant drawing that is actually drawable.
 *
 * `iconSet` is operator-authored JSON and reaches the client without a schema
 * parse on the read path, so half a glyph is a thing that can happen. Checking
 * it here is what keeps the promise the resolution order makes: a name nobody
 * can answer reaches the monogram. Trusting the key and reading `.paths` off
 * whatever is there white-screens every question step for that tenant instead.
 */
function drawable(set: IconSet, name: string | undefined): TenantIconGlyph | undefined {
  if (!name || !Object.prototype.hasOwnProperty.call(set, name)) return undefined
  const glyph = set[name]
  if (!glyph || !Array.isArray(glyph.paths) || glyph.paths.length === 0) return undefined
  if (!glyph.paths.every((d) => typeof d === 'string' && d.length > 0)) return undefined
  return glyph
}

/** Which of the three sources answered a name. Exported for the icon tests. */
export function resolveIconSource(name: string | undefined, set: IconSet): 'tenant' | 'vocabulary' | 'monogram' {
  if (drawable(set, name)) return 'tenant'
  if (name && isKnown(name)) return 'vocabulary'
  return 'monogram'
}

export function IconGlyph({ name, label, size = 44, className }: IconGlyphProps): JSX.Element {
  const set = useIconSet()
  const letter = label.trim() ? ([...label.trim()][0] ?? '·') : '·'
  const drawn = drawable(set, name)
  const source = drawn ? 'tenant' : name && isKnown(name) ? 'vocabulary' : 'monogram'
  return (
    <span className={className} style={{ display: 'inline-flex', width: size, height: size }}>
      {drawn ? (
        <DrawnGlyph glyph={drawn} size={size} />
      ) : source === 'vocabulary' ? (
        (() => {
          const Glyph = glyphs[name as IconName]
          return <Glyph size={size} />
        })()
      ) : (
        <MonogramGlyph seed={name ?? label} initial={letter} size={size} />
      )}
    </span>
  )
}
